import JsPDF from 'jspdf';
import { EstimationGenerated } from './EstimationGenerated';
import { Button, Form, Col, Spinner, Row, Modal } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import { GarageType, ProjectType, UnitType, WarehouseType } from '../../interfaces/project';
import { NumberInput } from '../ui/Forms/NumberInput';
import { useCurrency } from '../../hooks/currency/useCurrency';
import SpecialSelect from '../ui/SpecialSelect';
import { getBase64ImageFromUrl, formatMoney } from '../../utils/Functions';
import useUser from '../../hooks/user/useUser';
import { sendEmail } from '../../data/api';
import { EstimatorBodyType } from '../../interfaces/email';
import { useGoogleAnalytics } from '../../hooks/googleAnalytics/useGoogleAnalytics';
import useConfig from '../../hooks/config/useConfig';
import { isEcasaProject, isEstimatorDecimalProject, parseMaxDiscount } from '../../utils/estimatorConstants';

const CURRENCY_TO_ESTIMATE = 'UF'

const getProjectId = (project: ProjectType): number =>
    Number(project.id || project.identifier);

// Type definition for discount options
type DiscountType = 'Nada' | 'Bono Pie' | 'Descuento';

export interface EstimatorData {
    unit?: UnitType,
    unit_id: number,
    garage?: GarageType,
    garage_id?: number,
    warehouse?: WarehouseType,
    warehouse_id?: number,
    price_selected: any,
    price: string,
    garage_price_selected: any,
    garage_price: string,
    warehouse_price_selected: any,
    warehouse_price: string,
    discount_type: DiscountType,
    discount_value: string,
    bank_preapproval: string,
    client_name: string,
    client_rut: string,
    client_phone: string,
    bank_financing: number,
    unit_plan: string,
    inmo_img: string,
    exchange_rate: number,
    garage_exchange_rate?: number,
    warehouse_exchange_rate?: number,
    pie_bonus_conditions?: string,
}

export interface EstimatorProps {
    project: ProjectType;
    unit?: UnitType;
    pie?: number;
}

export const Estimator = (
    { project, unit, pie = 80 }: EstimatorProps
) => {
    // Estado para controlar la visibilidad del modal
    const [showModal, setShowModal] = useState(false);

    // Funciones para abrir y cerrar el modal
    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    // Define el color naranjo que se usa en el botón de "Generar cotización"
    const orangeColor = '#FF7A33'; // Asegúrate de que este sea el mismo color que el botón de "Generar cotización"

    // Función helper para obtener el valor del bono pie (prioriza unidad sobre proyecto)
    const getBonusPieValue = (selectedUnit?: UnitType): string => {
        if (isEcasaProject(getProjectId(project))) {
            return parseMaxDiscount(selectedUnit?.max_discount);
        }
        if (selectedUnit?.bonus_pie && selectedUnit.bonus_pie.trim() !== '' && selectedUnit.bonus_pie !== "0") {
            return selectedUnit.bonus_pie;
        }
        return project.pie_bonus_conditions || "20";
    };

    // Función helper para verificar si hay bono pie disponible
    const hasBonusPieAvailable = (selectedUnit?: UnitType): boolean => {
        if (isEcasaProject(getProjectId(project))) {
            const max = parseFloat(parseMaxDiscount(selectedUnit?.max_discount));
            return !isNaN(max) && max > 0;
        }
        if (selectedUnit?.bonus_pie && selectedUnit.bonus_pie.trim() !== '' && selectedUnit.bonus_pie !== "0") {
            return true;
        }
        if (project.pie_bonus_conditions && project.pie_bonus_conditions.trim() !== '' && project.pie_bonus_conditions !== "0") {
            return true;
        }
        return false;
    };

    const initialData: EstimatorData = {
        unit: unit || project.units[0] || undefined,
        unit_id: unit?.id || project.units[0]?.id,
        garage: undefined,
        warehouse: undefined,
        garage_id: project.garages ? project.garages[0]?.id : undefined,
        warehouse_id: project.warehouses ? project.warehouses[0]?.id : undefined,
        price_selected: '',
        price: '',
        garage_price_selected: '',
        garage_price: '',
        warehouse_price_selected: '',
        warehouse_price: '',
        discount_type: 'Nada',
        discount_value: '',
        bank_preapproval: pie ? pie.toString() : '80',
        client_name: '',
        client_rut: '',
        client_phone: '',
        bank_financing: 0,
        unit_plan: '',
        inmo_img: '',
        exchange_rate: 0,
        garage_exchange_rate: 0,
        warehouse_exchange_rate: 0
    }

    const defaultMessages = {
        unit_id: 'Este campo es requerido',
        price_selected: 'Este campo es requerido',
        price: 'Este campo es requerido',
        garage_id: 'Este campo es requerido',
        garage_price_selected: 'Este campo es requerido',
        garage_price: 'Este campo es requerido',
        warehouse_id: 'Este campo es requerido',
        warehouse_price_selected: 'Este campo es requerido',
        warehouse_price: 'Este campo es requerido',
        client_name: 'Este campo es requerido',
        client_rut: 'Este campo es requerido',
        client_phone: 'Este campo es requerido',
        discount_value: 'Este campo es requerido',
    }
    const [fieldsErrorMessages, setFieldsErrorMessages] = useState(defaultMessages)


    const componentRef = useRef(null);
    const { convertCurrency } = useCurrency()
    const [validated, setValidated] = useState(false)
    const [optionsUnits, setOptionsUnits] = useState<any[]>([])
    const [errorMessage, setErrorMessage] = useState('')
    const [pageHeight, setPageHeight] = useState(0)

    const [data, setData] = useState(initialData)
    const [loading, setLoading] = useState(false)
    const { userState } = useUser()
    const { country, translation } = useConfig()

    const GA = useGoogleAnalytics()

    const garagesOptions = project.garages?.map(g => ({ value: g.id, label: g.name })) || []
    const warehousesOptions = project.warehouses?.map(g => ({ value: g.id, label: g.name })) || []

    const [ufValue, setUfValue] = useState<number>(0);

    useEffect(() => {
        fetch('https://mindicador.cl/api/uf')
            .then(response => response.json())
            .then(data => {
                setUfValue(data.serie[0].valor);
            })
            .catch(error => console.error('Error fetching UF value:', error));
    }, []);

    useEffect(() => {
        let unitsOptions: any[] = []

        unitsOptions = unitsOptions.concat(project.units.map(u => {
            return { value: u.id, label: u.number }
        }))
        setOptionsUnits(unitsOptions)
    }, [])

    useEffect(() => {
        if (data.unit?.list_price && !data.price) {
            setData(prev => ({
                ...prev,
                price_selected: data.unit?.list_price,
                price: String(data.unit?.list_price)
            }))
        }
    }, [data.unit])

    const calculatePriceWithDiscount = (originalPrice: number) => {
        if (!originalPrice) return 0;
        
        let finalPrice = originalPrice;
        const projectId = getProjectId(project);
        // COMENTADO: El bono pie NO debe sumarse al precio, debe ser parte del 100% del total
        // Si es Bono Pie y el proyecto es especial, AUMENTAR el precio
        // if (data.discount_type === 'Bono Pie' && data.discount_value && [2553, 2554, 2555, 2558, 2884, 2975, 3031, 3219, 3220, 3253, 3260, 3338, 3378, 3398, 3402, 3407, 3408, 3409].includes(projectId)) {
        //     // Convertir valor con coma a punto para procesamiento si es proyecto especial
        //     const bonusValueStr = data.discount_value.replace(',', '.');
        //     const bonusValue = Number(bonusValueStr);
        //     if (!isNaN(bonusValue) && bonusValue > 0) {
        //         // Calcular el bono como porcentaje del precio original
        //         const bonusAmount = (bonusValue / 100) * originalPrice;
        //         finalPrice = originalPrice + bonusAmount;
        //     }
        // } else if (data.discount_type === 'Descuento' && data.discount_value) {
        if (data.discount_type === 'Descuento' && data.discount_value) {
            // Para descuentos, también manejar decimales si es proyecto especial
            const discountValueStr = isEstimatorDecimalProject(projectId) ? data.discount_value.replace(',', '.') : data.discount_value;
            const discountValue = Number(discountValueStr);
            if (!isNaN(discountValue) && discountValue > 0) {
                // Calcular el descuento como porcentaje del precio original
                const discountAmount = (discountValue / 100) * originalPrice;
                finalPrice = originalPrice - discountAmount;
            }
        }
        
        return finalPrice;
    };
    const setValue = (e: any) => {
        let newValue = e.target.value

        setData((prev) => ({ ...prev, [e.target.name]: newValue }))

        if (e.target.name == 'unit_id') {
            let unitData = project.units.find(u => u.id == newValue)
            if (unitData && unitData.list_price) {
                setData(prev => ({ 
                    ...prev, 
                    unit: unitData, 
                    price: String(unitData?.list_price), 
                    price_selected: unitData?.list_price, 
                    exchange_rate: 0 
                }))
            } else {
                setData(prev => ({ ...prev, unit: unitData, price: '', price_selected: '', exchange_rate: 0 }))
            }
        }

        if (e.target.name == 'garage_id') {
            let garageData = project.garages?.find(u => u.id == newValue)

            setData(prev => (
                {
                    ...prev,
                    garage: garageData,
                    garage_price:  '',
                    garage_price_selected: '',
                    garage_exchange_rate : 0
                }))
        }
        if (e.target.name == 'warehouse_id') {
            let warehouseData = project.warehouses?.find(u => u.id == newValue)
            setData(prev => (
                {
                    ...prev,
                    warehouse: warehouseData,
                    warehouse_price:  '',
                    warehouse_price_selected:   '',
                    warehouse_exchange_rate : 0
                }))
        }

        if (e.target.name == 'price_selected') {
            if (newValue == 'Otro' || newValue == 'Ninguno') setData(prev => ({ ...prev, price: '' }))
            else setData(prev => ({ ...prev, price: newValue }))
        }

        if (e.target.name == 'garage_price_selected') {
            if (newValue == 'Otro' || newValue == 'Ninguno') setData(prev => ({ ...prev, garage_price: '' }))
            else setData(prev => ({ ...prev, garage_price: newValue }))
        }

        if (e.target.name == 'warehouse_price_selected') {
            if (newValue == 'Otro' || newValue == 'Ninguno') setData(prev => ({ ...prev, warehouse_price: '' }))
            else setData(prev => ({ ...prev, warehouse_price: newValue }))
        }

        if (e.target.name == 'discount_type') {
            if (newValue == 'Nada') {
                setData(prev => ({ ...prev, discount_value: '' }))
            }
        }

        if (e.target.name == 'discount_value') {
            if (e.target.name == 'discount_type' && newValue == 'Nada') {
                setData(prev => ({ ...prev, discount_value: '' }))
            }
        }
    }


    const resetForm = (form: any) => {
        Object.keys(defaultMessages).forEach(key => {
            try {
                form[key]?.setCustomValidity('')
            } catch (error: any) {
                console.log(error.toString())
            }

        })
        setFieldsErrorMessages(defaultMessages);
    }

    const sendMailEstimator = () => {
        if (data.unit) {
            const dataEmail: EstimatorBodyType = {
                form_type: 'estimator',
                countryCode: country,
                unit: data.unit.identifier,
                unit_number: data.unit.number,
                garage: data.garage?.identifier,
                garage_name: data.garage?.name,
                garage_price: data.garage_price,
                warehouse: data.warehouse?.identifier,
                warehouse_name: data.warehouse?.name,
                warehouse_price: data.warehouse_price,
                id_project: project.identifier,
                title_project: project.title,
                currency: CURRENCY_TO_ESTIMATE,
                price: data.price,
                name: data.client_name,
                pie: data.bank_preapproval,
                id_inmo: userState.id,
                name_inmo: `${userState.name} - ${userState.real_estate}`,
                email_inmo: userState.email,
                phone_inmo: userState.phone,
                project_country: project.country
            } as EstimatorBodyType;
            sendEmail(dataEmail)
        }

    }
    const handleGenerate = async (ev: any) => {

        ev.preventDefault()
        setErrorMessage('')
        let form = ev.currentTarget;

        Object.keys(data).forEach(key => {
            try {
                form[key]?.setCustomValidity('')
            } catch (error: any) { console.log("error aqui : " + key + " " + error.toString()) }

        })

        let priceUF = data.price
        let validPrice = true

        //Si eligió uno de los que tiene la unidad
        if (data.price_selected != 'Otro' && data.unit?.currency && data.unit?.currency != CURRENCY_TO_ESTIMATE) {
            validPrice = false
            let _data = { ...data }
            const convert = convertCurrency(data.unit?.currency, CURRENCY_TO_ESTIMATE, parseFloat(data.price))

            if (convert && convert.success) {
                priceUF = convert.value.toString()
                validPrice = true
                setData(prev => ({ ...prev, price: priceUF, exchange_rate: convert.exchange_rate_to_from }))
            }
        }

        //Si eligió uno de los que tiene el garage
        if (data.garage_price_selected != 'Otro' && data.garage?.currency && data.garage?.currency != CURRENCY_TO_ESTIMATE) {
            validPrice = false
            const convert = convertCurrency(data.garage?.currency, CURRENCY_TO_ESTIMATE, parseFloat(data.garage_price))

            if (convert && convert.success) {
                priceUF = convert.value.toString()
                validPrice = true
                setData(prev => ({ ...prev, garage_price: priceUF, garage_exchange_rate: convert.exchange_rate_to_from }))
            }
        }

        //Si eligió uno de los que tiene la bodega
        if (data.warehouse_price_selected != 'Otro' && data.warehouse?.currency && data.warehouse?.currency != CURRENCY_TO_ESTIMATE) {
            validPrice = false
            const convert = convertCurrency(data.warehouse?.currency, CURRENCY_TO_ESTIMATE, parseFloat(data.warehouse_price))

            if (convert && convert.success) {
                priceUF = convert.value.toString()
                validPrice = true
                setData(prev => ({ ...prev, warehouse_price: priceUF, warehouse_exchange_rate: convert.exchange_rate_to_from }))
            }
        }

        if (!validPrice) {
            setErrorMessage('No se pudo convertir a ' + CURRENCY_TO_ESTIMATE)
            return false;
        }

        if (data.discount_type === 'Bono Pie' || data.discount_type === 'Descuento') {
        // Eliminar el símbolo '%' si existe y luego convertir a número
        const maxDiscountStr = data.unit?.max_discount || "0";
        const maxDiscount = Number(maxDiscountStr.replace('%', ''));
        
        // Verificar si es proyecto especial para manejar decimales
        const projectId = getProjectId(project);
        const isDecimalProject = isEstimatorDecimalProject(projectId);
        
        // Convertir valor con coma a punto para procesamiento si es proyecto especial
        const discountValueStr = isDecimalProject ? data.discount_value.replace(',', '.') : data.discount_value;
        const discountValue = Number(discountValueStr);
    
        if (isNaN(discountValue) || discountValue <= 0) {
            setFieldsErrorMessages(prev => ({ ...prev, discount_value: 'El valor debe ser mayor a 0' }))
            form['discount_value']?.setCustomValidity('invalid')
            return false;
        }
        
        if (data.discount_type === 'Bono Pie') {
            const maxBonusPie = parseFloat(getBonusPieValue(data.unit));
            const minValue = isDecimalProject ? 0.1 : 1;
            if (discountValue < minValue || discountValue > maxBonusPie) {
                setFieldsErrorMessages(prev => ({ ...prev, discount_value: `El valor debe estar entre ${minValue} y ${maxBonusPie}` }))
                form['discount_value']?.setCustomValidity('invalid')
                return false;
            }
        }
        
        if (data.discount_type === 'Descuento') {
            if (isNaN(maxDiscount)) {
                // Si maxDiscount no es un número, mostrar un error específico
                setFieldsErrorMessages(prev => ({ ...prev, discount_value: 'Valor máximo de descuento inválido' }))
                form['discount_value']?.setCustomValidity('invalid')
                return false;
            }
            
            if (discountValue > maxDiscount) {
                setFieldsErrorMessages(prev => ({ ...prev, discount_value: `El valor máximo es ${maxDiscount}${maxDiscount % 1 !== 0 ? '' : '%'}` }))
                form['discount_value']?.setCustomValidity('invalid')
                return false;
            }
        }
    }

        resetForm(form)
        setValidated(true);
        let validForm = form.checkValidity()

        console.log("data ", data)

        if (validForm) {
            setLoading(true)

            const report = new JsPDF({
                orientation: 'portrait',
                unit: 'px',
                hotfixes: ['px_scaling']
            });
            const widthPage = report.internal.pageSize.getWidth();
            const heightPage = report.internal.pageSize.getHeight();
            setPageHeight(heightPage - 1)

            //Convertir imágenes
            if (data.unit?.plan) {
                await getBase64ImageFromUrl(data.unit?.plan).then(res => {

                    if (res && typeof res == 'string') {
                        setData(prev => ({ ...prev, unit_plan: res }))
                    }
                }).catch(err => console.log(err))
            }

            if (userState.image) {
                await getBase64ImageFromUrl(userState.image).then(res => {

                    if (res && typeof res == 'string') {
                        setData(prev => ({ ...prev, inmo_img: res }))
                    }
                }).catch(err => console.log(err))
            }


            try {

                const fileName = `Cotización ${project.title} Unidad ${data.unit?.number} .pdf`
                const element = componentRef.current
                if (element) {

                    //@ts-ignore
                    report.html(element, {
                        autoPaging: 'text',
                        width: widthPage,
                        windowWidth: widthPage,

                    }).then(() => {
                        setLoading(false)
                        report.save(fileName);
                    }).catch(err => {
                        setLoading(false)
                        console.log(err)
                    })
                } else {
                    setLoading(false)
                }

            } catch (error) {
                console.error(error)
                setLoading(false)
            } finally {

                sendMailEstimator()
                GA.Event({ category: "User estimates unit", action: "user_estimates_unit", label: project.title , value: Number(userState.id) })

            }
        }

    }

    useEffect(() => {
        if (data.bank_preapproval) {
            setData(prev => ({ ...prev, bank_financing: parseInt(data.bank_preapproval) }))
        }
    }, [data.bank_preapproval])

    
    return (
        <div className="container">
            {/* Botón para abrir el modal */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className='fs-5 fw-bold mb-3 text-center'>Cotizador</h2>
                <Button 
                    variant="warning" 
                    onClick={handleShow} 
                    style={{ 
                        backgroundColor: orangeColor,
                        borderColor: '#e66a00',
                        color: '#fff', 
                        borderRadius: '10px',
                        padding: '6px 12px',
                        transition: 'background-color 0.3s, transform 0.3s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e66a00'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = orangeColor}
                >
                    Ver Condiciones Comerciales
                </Button>
            </div>

            {/* Modal de condiciones comerciales */}
            <Modal show={showModal} onHide={handleClose} centered backdrop="static">
                <Modal.Header closeButton style={{ backgroundColor: orangeColor, color: '#fff' }}>
                    <Modal.Title>Condiciones comerciales</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px', fontSize: '1rem', lineHeight: '1.5' }}>
                    <p><strong>Valor UF a la fecha de la cotización:</strong> {ufValue} CLP</p>
                    <p><strong>Reserva:</strong> {
                        project.deposit ? 
                            project.deposit.toString().includes('UF') ? 
                                project.deposit // Si ya incluye "UF", mostrar tal cual
                                : `${project.deposit} CLP` // Si no incluye "UF", agregar "CLP"
                            : 'No disponible'
                    }</p>
                    <p><strong>Bono Pie:</strong> {hasBonusPieAvailable(data.unit) ? `${getBonusPieValue(data.unit)}%` : 'No disponible'}</p>
                    {(project.up_front || project.commercial_conditions_description) && (
                        <p><strong>Condiciones comerciales:</strong> {
                            [project.up_front, project.commercial_conditions_description]
                                .filter(Boolean) // Filtra los valores null
                                .join(', ') // Une los valores con coma y espacio
                        }</p>
                    )}
                </Modal.Body>
            </Modal>

            <div className="row">
                <div className="col ">
                    <Form onSubmit={handleGenerate} noValidate validated={validated}>

                        <Row className="mb-3">
                            <Col lg="12">
                                <h5 className="fs-6 fw-bold text-secondary">Información del Cliente</h5>
                            </Col>
                            <Col lg="4" sm="12">
                                <Form.Group controlId="client_name">
                                    <Form.Label className="fw-bold text-secondary">Nombre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="client_name"
                                        value={data.client_name}
                                        onChange={setValue}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldsErrorMessages.client_name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col lg="4" sm="12">
                                <Form.Group controlId="client_phone">
                                    <Form.Label className="fw-bold text-secondary">Teléfono</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="client_phone"
                                        value={data.client_phone}
                                        onChange={setValue}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldsErrorMessages.client_phone}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col lg="4" sm="12">
                                <Form.Group controlId="client_rut">
                                    <Form.Label className="fw-bold text-secondary">RUT</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="client_rut"
                                        value={data.client_rut}
                                        onChange={setValue}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {fieldsErrorMessages.client_rut}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col lg="6" sm="12">
                                <Form.Group controlId="unit_id">
                                    <Form.Label className="fw-bold text-secondary">Unidad</Form.Label>
                                    <SpecialSelect
                                        options={optionsUnits}
                                        onChange={(option: any) => setValue({ target: { name: 'unit_id', value: option.value } })}
                                        selected={{ value: data.unit_id, label: data.unit?.number }}
                                        placeholder="Elige una unidad..."
                                        noOptionsMessage={'No se encontraron unidades'}
                                        setInvalid={!data.unit_id ? true : false}
                                    />
                                </Form.Group>

                            </Col>

                            <Col lg="6" sm="12">
                                <Form.Group controlId="price">
                                    <Form.Label className="fw-bold text-secondary">Precio</Form.Label>
                                    <div className='d-flex flex-column'>
                                        {data.unit && (
                                            <>
                                                <Form.Control
                                                    readOnly
                                                    value={`${data.unit?.currency} ${data.unit?.list_price}`}
                                                    onChange={() => {}}
                                                />
                                                {data.discount_type === 'Descuento' && data.discount_value && data.price && (
                                                    <div className="mt-2 text-success">
                                                        <strong>Precio con descuento ({data.discount_value}%):</strong> {data.unit?.currency} {formatMoney(calculatePriceWithDiscount(parseFloat(data.price)))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className='mt-0 mt-md-2'>

                            <Col lg="6" sm="12">
                                <Form.Group controlId="garage_id">
                                    <Form.Label className="fw-bold text-secondary">{translation.garage}</Form.Label>
                                    <SpecialSelect
                                        options={garagesOptions}
                                        onChange={(option: any) => setValue({ target: { name: 'garage_id', value: option.value } })}
                                        selected={{ value: data.garage_id, label: data.garage?.name }}
                                        placeholder={`Elige una ${translation.garage}`}
                                        noOptionsMessage={'No se encontraron estacionamientos'}
                                    />

                                </Form.Group>
                            </Col>

                            <Col lg="6" sm="12">


                                <Form.Group controlId="garage_price" >
                                    <Form.Label className="fw-bold text-secondary">Precio</Form.Label>
                                    <div className='d-flex align-items-start'>
                                        <Form.Select name='garage_price_selected'
                                            className=" w-50 " style={{ flex: 1 }}
                                            value={data.garage_price_selected}
                                            onChange={setValue}>
                                            <option disabled value={''} >Elige un precio</option>
                                            {
                                                data.garage &&
                                                <option value={data.garage?.price}>
                                                    Precio: {data.garage?.currency} {data.garage?.price}
                                                </option>
                                            }

                                        </Form.Select>
                                    </div>
                                </Form.Group>



                            </Col>

                        </Row>

                        <Row className='mt-0 mt-md-2'>

                            <Col lg="6" sm="12">
                                <Form.Group controlId="warehouse_id">
                                    <Form.Label className="fw-bold text-secondary">{translation.warehouse}</Form.Label>
                                    <SpecialSelect
                                        options={warehousesOptions}
                                        onChange={(option: any) => setValue({ target: { name: 'warehouse_id', value: option.value } })}
                                        selected={{ value: data.warehouse_id, label: data.warehouse?.name }}
                                        placeholder={`Elige una ${translation.warehouse}`}
                                        noOptionsMessage={'No se encontraron ' + translation.warehouses.toLowerCase()}
                                    />

                                </Form.Group>
                            </Col>

                            <Col lg="6" sm="12">

                                <Form.Group controlId="warehouse_price" >
                                    <Form.Label className="fw-bold text-secondary">Precio</Form.Label>
                                    <div className='d-flex align-items-start'>
                                        <Form.Select name='warehouse_price_selected' 
                                            className=" w-50 " style={{ flex: 1 }}
                                            value={data.warehouse_price_selected}
                                            onChange={setValue}>
                                            <option disabled value={''} >Elige un precio</option>
                                            {
                                                data.warehouse &&
                                                <option value={data.warehouse?.price}>
                                                    Precio: {data.warehouse?.currency} {data.warehouse?.price}
                                                </option>
                                            }
                                            
                                        </Form.Select>
                                    </div>
                                </Form.Group>

                            </Col>

                        </Row>

                        {(hasBonusPieAvailable(data.unit) || data.unit?.max_discount) && (
                            <Row className='mt-3'>
                                <Col lg="6" sm="12">
                                    <Form.Group controlId="discount_type">
                                        <Form.Label className="fw-bold text-secondary">Tipo de Descuento</Form.Label>
                                        <div className="d-flex gap-3 align-items-center">
                                            <div className="d-flex align-items-center">
                                                <Button 
                                                    variant={data.discount_type === 'Nada' ? 'primary' : 'outline-secondary'}
                                                    onClick={() => setValue({ target: { name: 'discount_type', value: 'Nada' } })}
                                                    className="rounded-circle d-flex justify-content-center align-items-center p-0"
                                                    style={{ 
                                                        width: '12px', 
                                                        height: '12px', 
                                                        fontSize: '0px',
                                                        borderRadius: '50%'
                                                    }}
                                                >
                                                </Button>
                                                <span className="ms-2">No</span>
                                            </div>
                                            {hasBonusPieAvailable(data.unit) && (
                                                <div className="d-flex align-items-center">
                                                    <Button 
                                                        variant={data.discount_type === 'Bono Pie' ? 'primary' : 'outline-secondary'}
                                                        onClick={() => setValue({ target: { name: 'discount_type', value: 'Bono Pie' } })}
                                                        className="rounded-circle d-flex justify-content-center align-items-center p-0"
                                                        style={{ 
                                                            width: '12px', 
                                                            height: '12px', 
                                                            fontSize: '0px',
                                                            borderRadius: '50%'
                                                        }}
                                                    >
                                                    </Button>
                                                    <span className="ms-2">Bono Pie</span>
                                                </div>
                                            )}
                                            {data.unit?.max_discount && (
                                                <div className="d-flex align-items-center">
                                                    <Button 
                                                        variant={data.discount_type === 'Descuento' ? 'primary' : 'outline-secondary'}
                                                        onClick={() => setValue({ target: { name: 'discount_type', value: 'Descuento' } })}
                                                        className="rounded-circle d-flex justify-content-center align-items-center p-0"
                                                        style={{ 
                                                            width: '12px', 
                                                            height: '12px', 
                                                            fontSize: '0px',
                                                            borderRadius: '50%'
                                                        }}
                                                    >
                                                    </Button>
                                                    <span className="ms-2">Descuento</span>
                                                </div>
                                            )}
                                        </div>
                                    </Form.Group>
                                </Col>
                                
                                <Col lg="6" sm="12">
                                    {data.discount_type !== 'Nada' && (
                                        <Form.Group controlId="discount_value">
                                            <Form.Label className="fw-bold text-secondary">
                                                Valor del {data.discount_type}
                                                {data.discount_type === 'Bono Pie' && hasBonusPieAvailable(data.unit) && (
                                                    <small className="text-muted ms-2">(Máximo: {getBonusPieValue(data.unit)}%)</small>
                                                )}
                                                {data.discount_type === 'Descuento' && data.unit?.max_discount && (
                                                    <small className="text-muted ms-2">(Máximo: {data.unit.max_discount})</small>
                                                )}
                                            </Form.Label>
                                            {/* Verificar si es un proyecto especial */}
                                            {(() => {
                                                const projectId = getProjectId(project);
                                                const isDecimalProject = isEstimatorDecimalProject(projectId);
                                                
                                                if (isDecimalProject) {
                                                    // Para proyectos especiales: campo de entrada manual con decimales
                                                    return (
                                                        <Form.Control
                                                            type="text"
                                                            name="discount_value"
                                                            value={data.discount_value}
                                                            onChange={setValue}
                                                            placeholder="Ej: 15,5 o 15.5"
                                                            required
                                                            isInvalid={
                                                                data.discount_value !== '' && (
                                                                    (data.discount_type === 'Bono Pie' && 
                                                                        (parseFloat(data.discount_value.replace(',', '.')) > parseFloat(getBonusPieValue(data.unit)) || 
                                                                         parseFloat(data.discount_value.replace(',', '.')) <= 0)) ||
                                                                    (data.discount_type === 'Descuento' && 
                                                                        (parseFloat(data.discount_value.replace(',', '.')) > parseFloat(data.unit?.max_discount?.replace('%', '') || "0") || 
                                                                         parseFloat(data.discount_value.replace(',', '.')) <= 0))
                                                                )
                                                            }
                                                        />
                                                    );
                                                } else {
                                                    // Para proyectos normales: selector incremental
                                                    return (
                                                        <Form.Select
                                                            name="discount_value"
                                                            value={data.discount_value}
                                                            onChange={setValue}
                                                            required
                                                            isInvalid={
                                                                data.discount_value !== '' && (
                                                                    (data.discount_type === 'Bono Pie' && 
                                                                        (parseFloat(data.discount_value) > parseFloat(getBonusPieValue(data.unit)) || 
                                                                         parseFloat(data.discount_value) <= 0)) ||
                                                                    (data.discount_type === 'Descuento' && 
                                                                        (parseFloat(data.discount_value) > parseFloat(data.unit?.max_discount?.replace('%', '') || "0") || 
                                                                         parseFloat(data.discount_value) <= 0))
                                                                )
                                                            }
                                                        >
                                                            <option value="">Seleccione valor</option>
                                                            {
                                                                (() => {
                                                                    let max = 0;
                                                                    let step = 1;
                                                                    if (data.discount_type === 'Bono Pie') {
                                                                        max = parseFloat(getBonusPieValue(data.unit));
                                                                        step = 1;
                                                                    } else if (data.discount_type === 'Descuento') {
                                                                        max = parseFloat(data.unit?.max_discount?.replace('%', '') || "0");
                                                                        step = 1;
                                                                    }
                                                                    let options: number[] = [];
                                                                    if (max > 0) {
                                                                        // Aplicar la misma lógica para todos los tipos (Bono Pie y Descuento)
                                                                        const maxInt = Math.floor(max);
                                                                        for (let i = 1; i <= maxInt; i++) {
                                                                            options.push(i);
                                                                        }
                                                                        // Si el máximo es decimal, agregar el valor completo
                                                                        if (max !== maxInt) {
                                                                            options.push(max);
                                                                        }
                                                                    }
                                                                    return options.map(val => (
                                                                        <option key={val} value={val}>{val}%</option>
                                                                    ));
                                                                })()
                                                            }
                                                        </Form.Select>
                                                    );
                                                }
                                            })()}
                                            {data.discount_value !== '' && data.discount_type === 'Bono Pie' && 
                                                parseFloat(data.discount_value.replace(',', '.')) > parseFloat(getBonusPieValue(data.unit)) && (
                                                <div className="text-danger small mt-1">
                                                    ⚠️ El valor máximo para Bono Pie es {getBonusPieValue(data.unit)}%
                                                </div>
                                            )}
                                            {data.discount_value !== '' && data.discount_type === 'Descuento' && 
                                                parseFloat(data.discount_value.replace(',', '.')) > parseFloat(data.unit?.max_discount?.replace('%', '') || "0") && (
                                                <div className="text-danger small mt-1">
                                                    ⚠️ El valor máximo para Descuento es {data.unit?.max_discount}
                                                </div>
                                            )}
                                            {data.discount_value !== '' && parseFloat(data.discount_value.replace(',', '.')) <= 0 && (
                                                <div className="text-danger small mt-1">
                                                    ⚠️ El valor debe ser mayor a 0
                                                </div>
                                            )}
                                            <Form.Control.Feedback type="invalid">
                                                {fieldsErrorMessages.discount_value}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Col>
                            </Row>
                        )}


                        <Row className='mt-3'>
                            <Col lg="6" sm="12">
                                <Form.Group controlId="bank_approval">
                                    <Form.Label className="fw-bold text-secondary">Preaprobación de Credito Bancario</Form.Label>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="d-flex align-items-center">
                                            <Button 
                                                variant={data.bank_preapproval !== '0' ? 'primary' : 'outline-secondary'}
                                                onClick={() => setValue({ target: { name: 'bank_preapproval', value: '80' } })}
                                                className="rounded-circle d-flex justify-content-center align-items-center p-0"
                                                style={{ width: '12px', height: '12px', fontSize: '0px' }}
                                            >
                                            </Button>
                                            <span className="ms-2">Aprobado</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <Button 
                                                variant={data.bank_preapproval === '0' ? 'primary' : 'outline-secondary'}
                                                onClick={() => setValue({ target: { name: 'bank_preapproval', value: '0' } })}
                                                className="rounded-circle d-flex justify-content-center align-items-center p-0"
                                                style={{ width: '12px', height: '12px', fontSize: '0px' }}
                                            >
                                            </Button>
                                            <span className="ms-2">No Aprobado</span>
                                        </div>
                                    </div>
                                </Form.Group>
                            </Col>
                            
                            {data.bank_preapproval !== '0' && (
                                <Col lg="6" sm="12">
                                    <Form.Group controlId="bank_preapproval_percentage">
                                        <Form.Label className="fw-bold text-secondary">Porcentaje de Aprobación</Form.Label>
                                        <Form.Select 
                                            name='bank_preapproval'
                                            value={data.bank_preapproval}
                                            onChange={setValue}>
                                            <option value="80">80%</option>
                                            <option value="85">85%</option>
                                            <option value="90">90%</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        <div className='text-center'>
                            <button className="btn btn-primary mt-4" type='submit' disabled={loading} >
                                Generar cotización
                                {
                                    loading &&
                                    <Spinner
                                        as="span"
                                        animation="grow"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                }

                            </button>
                            <div className="invalid-feedback d-block">
                                {errorMessage}
                            </div>
                        </div>

                    </Form>
                    


                    <div style={{ display: 'none' }}>
                        <div ref={componentRef}  >
                            {
                                data.unit &&
                                <EstimationGenerated
                                    project={project}
                                    allData={data}
                                    unit={data.unit}
                                    pie={parseFloat(data.bank_preapproval) || 0}
                                    price={calculatePriceWithDiscount(parseFloat(data.price))}
                                    unit_plan_encoded={data.unit_plan}
                                    inmo_logo_encoded={data.inmo_img}
                                    pageHeight={pageHeight}
                                    currency={CURRENCY_TO_ESTIMATE}
                                    exchange_rate={data.exchange_rate}
                                />
                            }

                        </div>

                    </div>

                </div>
            </div>

        </div>
    )
}